/**
 * Everything SellGPT keeps in the browser, and the single rule about where it goes.
 *
 * THE SALE DRAFT lives in localStorage. A customer who is halfway through selling a laptop and goes to
 * find the OTP on another device, or closes the tab by accident, must come back to their conversation
 * rather than to an empty page - sessionStorage would lose it in both cases.
 *
 * THE SESSION TOKEN lives in localStorage too, and that is a deliberate, stated trade-off rather than an
 * oversight. The alternative - an httpOnly cookie - is not available: sellgpt.in and portal.cashit.in are
 * different registrable domains, the API authenticates every client with a bearer JWT, and the CORS policy
 * does not allow credentials. So the token is reachable by any script that runs on this page, and the
 * mitigations that actually apply are the ones in place: the only third-party script on the page is
 * Google Analytics, loaded from googletagmanager.com and from nowhere else, no HTML from the API is ever
 * inserted as markup, and the token expires server-side. That analytics tag is worth naming rather than
 * glossing: it can read this token, so it is one vendor we are trusting, not zero.
 *
 * WHAT IS NEVER STORED, anywhere, for any length of time: the OTP itself. It exists only as the value of
 * a controlled input while it is being typed, and is dropped from memory as soon as it is verified or
 * rejected. It is never logged, never put in a URL, and never written to storage.
 */

/** Bumped when a stored shape changes, so an old draft is discarded rather than mis-read. */
const VERSION = 'v1'
const DRAFT_KEY = `sellgpt.draft.${VERSION}`
const AUTH_KEY = `sellgpt.auth.${VERSION}`

/**
 * Storage can throw rather than merely return null: Safari's private mode, a browser configured to block
 * site data, and an embedded webview all do it. SellGPT must still work when it does - the customer simply
 * loses the ability to recover a refresh - so every access is guarded and failure is silent by design.
 */
function read<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

function write(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* storage unavailable or full: the application keeps working, recovery does not */
  }
}

function remove(key: string): void {
  try {
    window.localStorage.removeItem(key)
  } catch {
    /* nothing to do and nothing to report */
  }
}

export const draftStorage = {
  read: <T>() => read<T>(DRAFT_KEY),
  write: (value: unknown) => write(DRAFT_KEY, value),
  clear: () => remove(DRAFT_KEY),
}

export const authStorage = {
  read: <T>() => read<T>(AUTH_KEY),
  write: (value: unknown) => write(AUTH_KEY, value),
  clear: () => remove(AUTH_KEY),
}
